import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThumbsUp, ThumbsDown, ExternalLink, Calendar, MessageSquare, Globe } from 'lucide-react';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'helpful', 'not-helpful', 'with-notes'
  const [locationFilter, setLocationFilter] = useState('all'); // 'all' or specific location
  const navigate = useNavigate();

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/decisions-with-feedback`,
        {
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include'
        }
      );

      if (response.ok) {
        const data = await response.json();
        setDecisions(data);
      } else {
        toast.error('Failed to load data');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const viewDecision = (decisionId) => {
    window.open(`/decide/${decisionId}/view`, '_blank');
  };

  // Get unique locations with counts
  const locationCounts = decisions.reduce((acc, d) => {
    const location = d.location ? 
      `${d.location.city}, ${d.location.region}, ${d.location.country}` : 
      'Unknown';
    acc[location] = (acc[location] || 0) + 1;
    return acc;
  }, {});
  const uniqueLocations = Object.entries(locationCounts).sort((a, b) => b[1] - a[1]);

  const filteredDecisions = decisions.filter(decision => {
    // Filter by feedback type
    let matchesFeedback = true;
    if (filter === 'helpful') matchesFeedback = decision.feedback?.helpful === true;
    if (filter === 'not-helpful') matchesFeedback = decision.feedback?.helpful === false;
    if (filter === 'with-notes') matchesFeedback = decision.feedback?.feedback_notes;
    
    // Filter by location
    let matchesLocation = true;
    if (locationFilter !== 'all') {
      const decisionLocation = decision.location ? 
        `${decision.location.city}, ${decision.location.region}, ${decision.location.country}` : 
        'Unknown';
      matchesLocation = decisionLocation === locationFilter;
    }
    
    return matchesFeedback && matchesLocation;
  });

  const helpfulCount = decisions.filter(d => d.feedback?.helpful === true).length;
  const notHelpfulCount = decisions.filter(d => d.feedback?.helpful === false).length;
  const withNotesCount = decisions.filter(d => d.feedback?.feedback_notes).length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner w-12 h-12"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Admin Dashboard
          </h1>
          <p className="text-gray-600">
            All decisions and feedback
          </p>
          
          {/* Feedback Filter Stats */}
          <div className="mt-4 flex gap-4 text-sm flex-wrap">
            <button
              onClick={() => setFilter('all')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                filter === 'all'
                  ? 'bg-purple-100 border-2 border-purple-500 text-purple-700 font-semibold'
                  : 'bg-white border-2 border-gray-200 hover:border-purple-300'
              }`}
            >
              <span className="font-semibold">{decisions.length}</span>
              <span className="text-gray-600">All Decisions</span>
            </button>
            <button
              onClick={() => setFilter('helpful')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                filter === 'helpful'
                  ? 'bg-turquoise-100 border-2 border-turquoise-500 text-turquoise-700 font-semibold'
                  : 'bg-white border-2 border-gray-200 hover:border-turquoise-300'
              }`}
            >
              <ThumbsUp className="w-4 h-4 text-turquoise-600" />
              <span className="font-semibold">{helpfulCount}</span>
              <span className="text-gray-600">Helpful</span>
            </button>
            <button
              onClick={() => setFilter('not-helpful')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                filter === 'not-helpful'
                  ? 'bg-pink-100 border-2 border-pink-500 text-pink-700 font-semibold'
                  : 'bg-white border-2 border-gray-200 hover:border-pink-300'
              }`}
            >
              <ThumbsDown className="w-4 h-4 text-pink-600" />
              <span className="font-semibold">{notHelpfulCount}</span>
              <span className="text-gray-600">Not Helpful</span>
            </button>
            <button
              onClick={() => setFilter('with-notes')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                filter === 'with-notes'
                  ? 'bg-purple-100 border-2 border-purple-500 text-purple-700 font-semibold'
                  : 'bg-white border-2 border-gray-200 hover:border-purple-300'
              }`}
            >
              <MessageSquare className="w-4 h-4 text-purple-600" />
              <span className="font-semibold">{withNotesCount}</span>
              <span className="text-gray-600">With Feedback</span>
            </button>
          </div>

          {/* Location Filter */}
          <div className="mt-4">
            <label className="block text-sm font-semibold mb-2">Filter by Location:</label>
            <select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
            >
              <option value="all">All Locations ({decisions.length} decisions)</option>
              {uniqueLocations.map(([location, count]) => (
                <option key={location} value={location}>
                  {location} ({count} decision{count !== 1 ? 's' : ''})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Decision List */}
        {filteredDecisions.length === 0 ? (
          <div className="glass-card p-8 text-center">
            <p className="text-gray-500">No decisions found for this filter</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredDecisions.map((decision) => (
              <div
                key={decision.id}
                className="glass-card p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                      {decision.title || 'Untitled Decision'}
                    </h3>
                    <div className="flex items-center gap-3 flex-wrap">
                      {decision.feedback ? (
                        decision.feedback.helpful ? (
                          <div className="flex items-center gap-2 px-3 py-1 bg-turquoise-50 text-turquoise-700 rounded-full border border-turquoise-500">
                            <ThumbsUp className="w-4 h-4" />
                            <span className="text-sm font-semibold">Helpful</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 px-3 py-1 bg-pink-50 text-pink-700 rounded-full border border-pink-500">
                            <ThumbsDown className="w-4 h-4" />
                            <span className="text-sm font-semibold">Not Helpful</span>
                          </div>
                        )
                      ) : (
                        <div className="flex items-center gap-2 px-3 py-1 bg-gray-50 text-gray-500 rounded-full border border-gray-300">
                          <span className="text-sm">No feedback yet</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-gray-500 text-sm">
                        <Calendar className="w-4 h-4" />
                        {formatDate(decision.created_at)}
                      </div>
                      <div className="flex items-center gap-2 text-gray-500 text-sm">
                        <Globe className="w-4 h-4" />
                        {decision.location ? 
                          `${decision.location.city}, ${decision.location.region}` : 
                          'Unknown Location'}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => viewDecision(decision.id)}
                    className="flex items-center gap-2 text-purple-600 hover:text-purple-700 text-sm font-medium ml-4"
                  >
                    <span>View</span>
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </div>

                {decision.feedback?.feedback_notes && (
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <p className="text-sm font-semibold text-gray-700 mb-2">User Notes:</p>
                    <p className="text-gray-700 whitespace-pre-wrap">{decision.feedback.feedback_notes}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
